import type { ApiClient } from '@carbonwebmail/api-client'
import { useCallback } from 'react'
import { when } from '../../ui/format.js'
import { EmptyCard, PagedScreen } from '../../ui/PagedScreen.js'
import { StatusPill } from '../../ui/StatusPill.js'
import { Td, Tr } from '../../ui/Table.js'
import { usePaged } from '../../ui/usePaged.js'
import { CONSENT } from './statusLabels.js'

/**
 * The consent record: what was asked, what was shown, who agreed, and when.
 *
 * Append-only. Nothing on this screen can be edited or deleted, and that is the
 * point — a record that can be tidied is not evidence. If a mailbox provider or
 * a regulator asks why a particular address was mailed, this is the answer, and
 * it is worth nothing if anyone could have changed it afterwards.
 */
export function ConsentPage({ client }: { readonly client: ApiClient }) {
  const load = useCallback((page: number) => client.consent(page), [client])
  const { state, setPage, retry } = usePaged(
    load,
    'We could not load your consent record. Please try again in a moment.',
  )

  return (
    <PagedScreen
      title="Consent record"
      intro="Append-only. Nothing here can be edited or removed, which is what makes it evidence."
      state={state}
      onRetry={() => void retry()}
      onPage={setPage}
      head={['When', 'Email', 'Event', 'Source', 'IP']}
      minWidth={720}
      empty={
        <EmptyCard heading="No consent events yet">
          Signups, confirmations and unsubscribes are recorded here as they happen. Contacts added
          by import carry the consent their source recorded, not a new one.
        </EmptyCard>
      }
      row={(item) => (
        <Tr key={`${item.email}-${item.occurredAt}-${item.type}`}>
          <Td>{when(item.occurredAt)}</Td>
          <Td mono>{item.email}</Td>
          <Td>
            <StatusPill tone={CONSENT[item.type].tone}>{CONSENT[item.type].label}</StatusPill>
          </Td>
          <Td>{item.source}</Td>
          {/*
            Null for anything with no request behind it — a bounce reported by a
            provider has no browser and therefore no address. An empty cell says
            that; a zero would not.
          */}
          <Td mono>{item.ip ?? '—'}</Td>
        </Tr>
      )}
    />
  )
}
